import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * State manager for persisting swiper state across page navigation
 */
class SwiperState {
  constructor(sectionId) {
    this.key = `swiper_state_${sectionId}`;
  }

  save(state) {
    try {
      const dataToSave = {
        currentIndex: state.currentIndex,
        history: state.history,
        likedProducts: state.likedProducts,
        currentCollection: state.currentCollection,
        products: state.products, // Save products too for restoration
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.key, JSON.stringify(dataToSave));
    } catch (e) {
      console.warn('State kaydetme hatası:', e);
    }
  }

  load() {
    try {
      const data = sessionStorage.getItem(this.key);
      if (!data) return null;
      
      const state = JSON.parse(data);
      // Expire after 30 minutes
      if (Date.now() - state.timestamp > 30 * 60 * 1000) {
        this.clear();
        return null;
      }
      return state;
    } catch {
      return null;
    }
  }

  clear() {
    try {
      sessionStorage.removeItem(this.key);
    } catch (e) {
      // Ignore
    }
  }
}

/**
 * @typedef {object} ProductSwiperRefs
 * @property {HTMLElement} cardStack
 * @property {HTMLElement} likedStack
 * @property {HTMLElement} rewindBtn
 * @property {HTMLElement} passBtn
 * @property {HTMLElement} likeBtn
 * @property {HTMLElement} emptyState
 * @property {HTMLElement} loadingState
 * @property {HTMLElement} currentCardInfo
 * @property {HTMLElement} progressFill
 * @property {HTMLElement} mobileCounter
 * 
 * @extends Component<ProductSwiperRefs>
 */
export class ProductSwiper extends Component {
  requiredRefs = ['cardStack'];

  /** @type {SwiperState} */
  stateManager;

  /** @type {Array<{id: string, variantId: string, title: string, price: string, comparePrice: string, image: string, url: string}>} */
  products = [];

  /** @type {number} */
  currentIndex = 0;

  /** @type {Array<{productId: string, variantId: string, action: 'like' | 'pass', index: number}>} */
  history = [];

  /** @type {Array<{id: string, variantId: string, title: string, image: string}>} */
  likedProducts = [];

  /** @type {string} */
  currentCollection = '';

  // Swipe state
  isDragging = false;
  startX = 0;
  startY = 0;
  offsetX = 0;
  offsetY = 0;

  // Constants
  SWIPE_THRESHOLD = 80;
  MAX_ROTATION = 12;

  /** @type {HTMLElement | null} */
  activeCard = null;

  /** @type {boolean} */
  isAnimating = false;

  // Progress milestones
  MILESTONE_1 = 5;
  MILESTONE_2 = 10;

  connectedCallback() {
    super.connectedCallback();
    
    const sectionId = this.dataset.sectionId || 'default';
    this.stateManager = new SwiperState(sectionId);
    
    this.#initializeProducts();
    this.#bindEvents();
    this.#bindCollectionEvents();
    
    // Save state before page unload
    window.addEventListener('beforeunload', this.#saveState);
    
    // Also save on visibility change (for mobile)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.#saveState();
      }
    });
    
    // Save state when clicking links
    this.addEventListener('click', (e) => {
      if (e.target.closest('a')) {
        this.#saveState();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#saveState();
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
    document.removeEventListener('touchmove', this.#handleTouchMove);
    document.removeEventListener('touchend', this.#handleTouchEnd);
    window.removeEventListener('beforeunload', this.#saveState);
  }

  #saveState = () => {
    if (this.products.length === 0) return;
    
    this.stateManager.save({
      currentIndex: this.currentIndex,
      history: this.history,
      likedProducts: this.likedProducts,
      currentCollection: this.currentCollection,
      products: this.products,
    });
  };

  #restoreState() {
    const savedState = this.stateManager.load();
    if (!savedState) return false;
    
    // Only restore if same collection or if we have saved products
    if (savedState.currentCollection && savedState.products && savedState.products.length > 0) {
      this.currentIndex = savedState.currentIndex || 0;
      this.history = savedState.history || [];
      this.likedProducts = savedState.likedProducts || [];
      this.currentCollection = savedState.currentCollection;
      this.products = savedState.products;
      return true;
    }
    
    return false;
  }

  #initializeProducts() {
    // First try to restore state
    const restored = this.#restoreState();
    
    if (restored) {
      // State was restored, render from saved state
      this.#renderCards();
      this.#updateLikedStack();
      this.#updateUI();
      
      // Update active collection pill
      const section = this.closest('.product-swiper-section');
      section?.querySelectorAll('.collection-pill').forEach(pill => {
        const isActive = pill.dataset.collection === this.currentCollection;
        pill.classList.toggle('active', isActive);
        pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      return;
    }
    
    // No saved state, initialize from data attributes
    const productsData = this.dataset.products;
    const collectionHandle = this.dataset.collection || '';
    
    if (productsData) {
      try {
        this.products = JSON.parse(productsData);
        this.currentCollection = collectionHandle;
        this.#renderCards();
        this.#updateUI();
      } catch (e) {
        console.error('Ürün verileri ayrıştırma hatası:', e);
      }
    }
  }

  #bindEvents() {
    this.refs.cardStack?.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    document.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.#handleTouchEnd);

    this.refs.cardStack?.addEventListener('mousedown', this.#handleMouseDown);
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
  }

  #bindCollectionEvents() {
    const section = this.closest('.product-swiper-section');
    const collectionNav = section?.querySelector('.collection-nav');
    
    if (collectionNav) {
      collectionNav.addEventListener('click', this.#handleCollectionClick);
    }
  }

  #handleCollectionClick = (e) => {
    const button = e.target.closest('.collection-pill');
    if (!button) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const collectionHandle = button.dataset.collection;
    if (!collectionHandle || collectionHandle === this.currentCollection) return;

    const section = this.closest('.product-swiper-section');
    section?.querySelectorAll('.collection-pill').forEach(pill => {
      pill.classList.remove('active');
      pill.setAttribute('aria-selected', 'false');
    });
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');

    this.#loadCollection(collectionHandle);
  };

  #handleTouchStart = (e) => {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    const touch = e.touches[0];
    this.#startDrag(touch.clientX, touch.clientY);
  };

  #handleTouchMove = (e) => {
    if (!this.isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    this.#moveDrag(touch.clientX, touch.clientY);
  };

  #handleTouchEnd = () => {
    if (!this.isDragging) return;
    this.#endDrag();
  };

  #handleMouseDown = (e) => {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    if (e.target.closest('a, button')) return;
    e.preventDefault();
    this.#startDrag(e.clientX, e.clientY);
  };

  #handleMouseMove = (e) => {
    if (!this.isDragging) return;
    this.#moveDrag(e.clientX, e.clientY);
  };

  #handleMouseUp = () => {
    if (!this.isDragging) return;
    this.#endDrag();
  };

  #startDrag(x, y) {
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    if (!this.activeCard) return;

    this.isDragging = true;
    this.startX = x;
    this.startY = y;
    this.offsetX = 0;
    this.offsetY = 0;

    this.activeCard.classList.add('dragging');
  }

  #moveDrag(x, y) {
    if (!this.activeCard) return;

    this.offsetX = x - this.startX;
    this.offsetY = (y - this.startY) * 0.4;

    const rotation = (this.offsetX / window.innerWidth) * this.MAX_ROTATION;
    
    this.activeCard.style.transform = `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) rotate(${rotation}deg)`;
    
    const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
    const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
    
    if (likeIndicator && passIndicator) {
      const progress = Math.min(Math.abs(this.offsetX) / this.SWIPE_THRESHOLD, 1);
      if (this.offsetX > 20) {
        likeIndicator.style.opacity = progress.toString();
        passIndicator.style.opacity = '0';
      } else if (this.offsetX < -20) {
        passIndicator.style.opacity = progress.toString();
        likeIndicator.style.opacity = '0';
      } else {
        likeIndicator.style.opacity = '0';
        passIndicator.style.opacity = '0';
      }
    }
  }

  #endDrag() {
    if (!this.activeCard) return;

    this.isDragging = false;
    this.activeCard.classList.remove('dragging');

    const shouldSwipeRight = this.offsetX > this.SWIPE_THRESHOLD;
    const shouldSwipeLeft = this.offsetX < -this.SWIPE_THRESHOLD;

    if (shouldSwipeRight) {
      this.#swipeCard('right');
    } else if (shouldSwipeLeft) {
      this.#swipeCard('left');
    } else {
      this.activeCard.style.transition = 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
      this.activeCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      
      const likeIndicator = this.activeCard.querySelector('.swipe-indicator--like');
      const passIndicator = this.activeCard.querySelector('.swipe-indicator--pass');
      if (likeIndicator) likeIndicator.style.opacity = '0';
      if (passIndicator) passIndicator.style.opacity = '0';
      
      setTimeout(() => {
        if (this.activeCard) {
          this.activeCard.style.transition = '';
        }
      }, 400);
    }
  }

  #swipeCard(direction) {
    if (!this.activeCard || this.isAnimating) return;
    
    this.isAnimating = true;
    const card = this.activeCard;
    
    const exitX = direction === 'right' ? window.innerWidth : -window.innerWidth;
    const exitRotation = direction === 'right' ? 20 : -20;

    const indicator = card.querySelector(direction === 'right' ? '.swipe-indicator--like' : '.swipe-indicator--pass');
    if (indicator) indicator.style.opacity = '1';

    card.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease';
    card.style.transform = `translate3d(${exitX}px, ${this.offsetY}px, 0) rotate(${exitRotation}deg)`;
    card.style.opacity = '0';
    card.style.pointerEvents = 'none';

    setTimeout(() => {
      if (direction === 'right') {
        this.#processLike();
      } else {
        this.#processPass();
      }
      
      card.remove();
      this.currentIndex++;
      this.#activateNextCard();
      this.#updateUI();
      this.isAnimating = false;
      this.#saveState();
    }, 500);
  }

  #activateNextCard() {
    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (!cards || cards.length === 0) {
      this.#showEmptyState();
      return;
    }

    cards.forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      if (index === 0) {
        card.classList.add('active');
        card.style.transform = '';
        card.style.opacity = '';
        card.style.transition = '';
      } else {
        card.classList.remove('active');
      }
    });

    const nextProductIndex = this.currentIndex + cards.length;
    if (nextProductIndex < this.products.length) {
      const product = this.products[nextProductIndex];
      const newCard = this.#createCard(product, cards.length);
      this.refs.cardStack?.appendChild(newCard);
    }
  }

  #processLike() {
    const product = this.products[this.currentIndex];
    if (!product) return;

    this.history.push({ 
      productId: product.id, 
      variantId: product.variantId, 
      action: 'like',
      index: this.currentIndex 
    });
    
    this.likedProducts.push({
      id: product.id,
      variantId: product.variantId,
      title: product.title,
      image: product.image,
    });
    
    this.#updateLikedStack();
    this.#addToCart(product.variantId);
    this.#checkMilestone();
  }

  #processPass() {
    const product = this.products[this.currentIndex];
    if (!product) return;

    this.history.push({ 
      productId: product.id, 
      variantId: product.variantId, 
      action: 'pass',
      index: this.currentIndex 
    });
  }

  #checkMilestone() {
    const count = this.likedProducts.length;
    
    if (count === this.MILESTONE_1) {
      this.#showMilestoneReached(1);
    } else if (count === this.MILESTONE_2) {
      this.#showMilestoneReached(2);
    }
  }

  #showMilestoneReached(milestone) {
    const toast = document.createElement('div');
    toast.className = 'milestone-toast';
    toast.innerHTML = milestone === 1 
      ? `<span class="milestone-toast__icon">🎉</span><span>5 Al 2 Öde kazandın!</span>`
      : `<span class="milestone-toast__icon">🎁</span><span>10 Al 4 Öde kazandın!</span>`;
    
    this.appendChild(toast);
    
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  }

  async #addToCart(variantId) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        headers: { Accept: 'application/json' },
      });

      const data = await response.json();

      if (!data.status) {
        this.dispatchEvent(
          new CartAddEvent({}, 'product-swiper', {
            source: 'product-swiper',
            variantId: variantId,
          })
        );
      }
    } catch (error) {
      console.error('Sepete eklenirken hata:', error);
    }
  }

  handleLike() {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('right');
  }

  handlePass() {
    if (this.isAnimating || this.currentIndex >= this.products.length) return;
    this.activeCard = this.refs.cardStack?.querySelector('.swipe-card.active');
    this.offsetY = 0;
    this.#swipeCard('left');
  }

  handleRewind() {
    if (this.history.length === 0 || this.isAnimating) return;

    const lastAction = this.history.pop();
    if (!lastAction) return;

    this.isAnimating = true;
    this.currentIndex--;

    if (lastAction.action === 'like') {
      this.#removeFromCart(lastAction.variantId);
      this.likedProducts = this.likedProducts.filter(p => p.id !== lastAction.productId);
      this.#updateLikedStack();
    }

    const cards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    if (cards && cards.length > 0) {
      cards[cards.length - 1]?.remove();
    }

    const product = this.products[this.currentIndex];
    const newCard = this.#createCard(product, 0);
    newCard.classList.add('active');
    
    const startX = lastAction.action === 'like' ? window.innerWidth : -window.innerWidth;
    newCard.style.transform = `translate3d(${startX}px, 0, 0) rotate(${lastAction.action === 'like' ? 20 : -20}deg)`;
    newCard.style.opacity = '0';
    
    this.refs.cardStack?.prepend(newCard);

    const allCards = this.refs.cardStack?.querySelectorAll('.swipe-card');
    allCards?.forEach((card, index) => {
      card.style.setProperty('--stack-index', index.toString());
      if (index === 0) {
        card.classList.add('active');
      } else {
        card.classList.remove('active');
      }
    });

    requestAnimationFrame(() => {
      newCard.style.transition = 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.3s ease';
      newCard.style.transform = 'translate3d(0, 0, 0) rotate(0deg)';
      newCard.style.opacity = '1';
    });

    setTimeout(() => {
      newCard.style.transition = '';
      this.isAnimating = false;
      this.#saveState();
    }, 500);

    this.#hideEmptyState();
    this.#updateUI();
  }

  async #removeFromCart(variantId) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 }),
      });
      
      this.dispatchEvent(
        new CartAddEvent({}, 'product-swiper', {
          source: 'product-swiper',
          variantId: variantId,
        })
      );
    } catch (error) {
      console.error('Sepetten çıkarılırken hata:', error);
    }
  }

  async #loadCollection(collectionHandle) {
    this.#showLoadingState();

    try {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=50`);
      const data = await response.json();

      this.products = data.products.map(product => ({
        id: product.id.toString(),
        variantId: product.variants[0]?.id?.toString() || '',
        title: product.title,
        // Shopify products.json returns prices as strings like "405.90", not cents
        price: this.#formatMoney(product.variants[0]?.price),
        comparePrice: product.variants[0]?.compare_at_price ? this.#formatMoney(product.variants[0].compare_at_price) : '',
        image: product.images[0]?.src || '',
        url: `/products/${product.handle}`,
      }));

      // Reset state for new collection
      this.currentIndex = 0;
      this.history = [];
      this.likedProducts = [];
      this.currentCollection = collectionHandle;
      
      // Clear old state and save new
      this.stateManager.clear();
      
      this.#renderCards();
      this.#updateLikedStack();
      this.#hideLoadingState();
      this.#updateUI();
      this.#saveState();
    } catch (error) {
      console.error('Koleksiyon yüklenirken hata:', error);
      this.#hideLoadingState();
    }
  }

  #formatMoney(price) {
    // Shopify products.json returns price as string like "405.90" or number
    // Parse it and format for Turkish locale
    let amount;
    if (typeof price === 'string') {
      // Remove any existing currency symbols and parse
      amount = parseFloat(price.replace(/[^0-9.,]/g, '').replace(',', '.'));
    } else {
      amount = parseFloat(price) || 0;
    }
    
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  #renderCards() {
    const cardStack = this.refs.cardStack;
    if (!cardStack) return;

    cardStack.innerHTML = '';

    const visibleProducts = this.products.slice(this.currentIndex, this.currentIndex + 3);
    
    if (visibleProducts.length === 0) {
      this.#showEmptyState();
      return;
    }

    this.#hideEmptyState();

    visibleProducts.forEach((product, index) => {
      const card = this.#createCard(product, index);
      if (index === 0) {
        card.classList.add('active');
      }
      cardStack.appendChild(card);
    });
  }

  #createCard(product, stackIndex) {
    const card = document.createElement('div');
    card.className = 'swipe-card';
    card.dataset.productId = product.id;
    card.style.setProperty('--stack-index', stackIndex.toString());
    
    const hasComparePrice = product.comparePrice && product.comparePrice !== product.price;

    card.innerHTML = `
      <div class="swipe-card__inner">
        <div class="swipe-card__image-wrapper">
          ${product.image 
            ? `<img src="${product.image}" alt="${product.title}" class="swipe-card__image" loading="eager" draggable="false" />`
            : `<div class="swipe-card__placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>`
          }
          <div class="swipe-indicator swipe-indicator--like">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
            <span>SEPETE EKLE</span>
          </div>
          <div class="swipe-indicator swipe-indicator--pass">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            <span>GEÇ</span>
          </div>
        </div>
        <div class="swipe-card__content">
          <h3 class="swipe-card__title">${product.title}</h3>
          <div class="swipe-card__price-row">
            <span class="swipe-card__price">${product.price}</span>
            ${hasComparePrice ? `<span class="swipe-card__compare-price">${product.comparePrice}</span>` : ''}
          </div>
          <a href="${product.url}" class="swipe-card__link">
            <span>Ürünü İncele</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </a>
        </div>
      </div>
    `;

    return card;
  }

  #updateLikedStack() {
    const likedStack = this.refs.likedStack;
    if (!likedStack) return;

    if (this.likedProducts.length === 0) {
      likedStack.innerHTML = `
        <div class="liked-stack__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>Beğendiğin ürünler burada görünecek</span>
        </div>
      `;
      return;
    }

    likedStack.innerHTML = '';
    
    const displayProducts = this.likedProducts.slice(-4);
    
    displayProducts.forEach((product, index) => {
      const item = document.createElement('div');
      item.className = 'liked-stack__item';
      item.style.setProperty('--stack-offset', (displayProducts.length - 1 - index).toString());
      item.innerHTML = `<img src="${product.image}" alt="${product.title}" draggable="false" />`;
      likedStack.appendChild(item);
    });

    const counter = document.createElement('div');
    counter.className = 'liked-stack__counter';
    counter.innerHTML = `<span>${this.likedProducts.length}</span>`;
    likedStack.appendChild(counter);
  }

  #updateUI() {
    this.#updateRewindButton();
    this.#updateCounter();
    this.#updateProgressBar();
    this.#updateMobileCounter();
  }

  #updateRewindButton() {
    const rewindBtn = this.refs.rewindBtn;
    if (rewindBtn) {
      const canRewind = this.history.length > 0;
      rewindBtn.disabled = !canRewind;
      rewindBtn.classList.toggle('disabled', !canRewind);
    }
  }

  #updateCounter() {
    const counter = this.refs.currentCardInfo;
    if (counter) {
      const remaining = Math.max(0, this.products.length - this.currentIndex);
      counter.textContent = `${remaining} ürün kaldı`;
    }
  }

  #updateProgressBar() {
    // Find progress elements in the section (they're outside this component)
    const section = this.closest('.product-swiper-section');
    if (!section) return;
    
    const progressFill = section.querySelector('.progress-bar__fill');
    const milestone1El = section.querySelector('.progress-milestone[data-milestone="1"]');
    const milestone2El = section.querySelector('.progress-milestone[data-milestone="2"]');
    const progressText = section.querySelector('.progress-text');
    const label1 = section.querySelector('.progress-label[data-for="1"]');
    const label2 = section.querySelector('.progress-label[data-for="2"]');
    
    const count = this.likedProducts.length;
    const maxMilestone = this.MILESTONE_2;
    const progress = Math.min((count / maxMilestone) * 100, 100);
    
    // Update progress bar fill
    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
    
    // Update milestone 1 state
    if (milestone1El) {
      const isReached = count >= this.MILESTONE_1;
      const isActive = count > 0 && count < this.MILESTONE_1;
      milestone1El.classList.toggle('reached', isReached);
      milestone1El.classList.toggle('active', isActive);
    }
    
    // Update milestone 2 state
    if (milestone2El) {
      const isReached = count >= this.MILESTONE_2;
      const isActive = count >= this.MILESTONE_1 && count < this.MILESTONE_2;
      milestone2El.classList.toggle('reached', isReached);
      milestone2El.classList.toggle('active', isActive);
    }
    
    // Update label colors
    if (label1) {
      label1.classList.toggle('reached', count >= this.MILESTONE_1);
    }
    if (label2) {
      label2.classList.toggle('reached', count >= this.MILESTONE_2);
    }
    
    // Update progress text
    if (progressText) {
      if (count >= this.MILESTONE_2) {
        progressText.textContent = 'Tüm ödülleri kazandın! 🎉';
      } else if (count >= this.MILESTONE_1) {
        progressText.textContent = `10 Al 4 Öde için ${this.MILESTONE_2 - count} ürün daha ekle!`;
      } else {
        progressText.textContent = `5 Al 2 Öde için ${this.MILESTONE_1 - count} ürün daha ekle!`;
      }
    }
  }

  #updateMobileCounter() {
    const mobileCounter = this.refs.mobileCounter;
    if (mobileCounter) {
      const count = this.likedProducts.length;
      mobileCounter.textContent = count.toString();
      mobileCounter.classList.toggle('has-items', count > 0);
    }
  }

  #showEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = false;
  }

  #hideEmptyState() {
    const emptyState = this.refs.emptyState;
    if (emptyState) emptyState.hidden = true;
  }

  #showLoadingState() {
    const loadingState = this.refs.loadingState;
    if (loadingState) loadingState.hidden = false;
    this.refs.cardStack?.classList.add('loading');
  }

  #hideLoadingState() {
    const loadingState = this.refs.loadingState;
    if (loadingState) loadingState.hidden = true;
    this.refs.cardStack?.classList.remove('loading');
  }
}

if (!customElements.get('product-swiper')) {
  customElements.define('product-swiper', ProductSwiper);
}
